import React from 'react';
import {PropTypes} from 'prop-types';
import DirectNavigationModel from '../DirectNavigationModel';
import NavigationView from '../NavigationView/NavigationView.react';
import {DropdownControlled} from '@homeaway/react-dropdown';
import './DropdownMenu.less';
import ArrowKeysNavigation from '../../a11y-tools/arrow-navigation';
import withLifecycleLogging from '../../../hoc/WithLifecycleLogging.react';

class DropdownMenu extends React.PureComponent {
    constructor(props) {
        super(props);
        this.state = {
            isDropdownOpen: false,
        };
        this.arrowKeysNavigation = new ArrowKeysNavigation();
        this.rootRef = React.createRef();
    }

    componentDidMount(): void {
        const node = this.rootRef.current;
        this.arrowKeysNavigation._unbindEvents();
        this.arrowKeysNavigation.focusableItems = DropdownMenu._determineFocusable(node);
        this.arrowKeysNavigation._bindEvents();
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const node = this.rootRef.current;
        /**
         * Why are we manually setting focusableItems?
         *
         * ArrowKeysNavigation is an undocumented class from a11y-tools; Its signature is
         * close to the signature of trapFocus, but much less usable. The constructor
         * requires an elementId, which is prone to duplication leading to runtime
         * errors in behavior. Because of this, we are eschewing the functionality
         * of its constructor and #determineFocusable methods and relying only on
         * the #handleArrowKeys method which operates on focusableItems.
         */
        this.arrowKeysNavigation._unbindEvents();
        this.arrowKeysNavigation.focusableItems = DropdownMenu._determineFocusable(node);
        this.arrowKeysNavigation._bindEvents();
    }

    static _determineFocusable(node) {
        return Array.from(
            node.querySelectorAll(
                'a[href], area[href], input, select, textarea, button:not([disabled]), [tabindex="0"]'
            )
        );
    }

    _close(focusToggle, automated) {
        if (this.state.isDropdownOpen) {
            this.setState({
                isDropdownOpen: false,
            });
            if (focusToggle) {
                this.arrowKeysNavigation.setFocusTo(0);
            }
            if (this.props.onClose) {
                this.props.onClose(automated);
            }
        }
    }

    _open() {
        if (!this.state.isDropdownOpen) {
            this.setState({
                isDropdownOpen: true,
            });
            if (this.props.onOpen) {
                this.props.onOpen();
            }
        }
    }

    _handleKeyDown = (e) => {
        if (e.keyCode === 27 && this.state.isDropdownOpen) {
            // ESC
            this._close(true, true);
        }
        this.arrowKeysNavigation.handleArrowKeys(e);
    };

    handleBlur = () => {
        this._close(false, true);
    };

    handleSpaceSelected(sphereId) {
        this._close(true, false);
        if (this.props.onSelectNavSpace) {
            this.props.onSelectNavSpace(sphereId);
        }
    }

    handleTouch = () => {
        this._close(false, true);
    };

    handleToggleClick = () => {
        if (this.state.isDropdownOpen) {
            this._close(false, false);
        } else {
            this._open();
        }
    };

    render() {
        return (
            <div className={`DropdownMenu`} ref={this.rootRef} onKeyDown={this._handleKeyDown}>
                <DropdownControlled
                    label={this.props.label}
                    customToggle={this.props.customToggle}
                    open={this.state.isDropdownOpen}
                    id={'DirectNavigationMenuPicker'}
                    onDropdownClick={this.handleToggleClick}
                    onBlur={this.handleBlur}
                    onTouch={this.handleTouch}
                    noArrow={this.props.noArrow}
                    tabIndex={0}
                >
                    <NavigationView
                        disabled={!this.state.isDropdownOpen}
                        navigation={this.props.navigation}
                        sphereId={this.props.sphereId}
                        onSelectNavSpace={(sphereId) => this.handleSpaceSelected(sphereId)}
                        shouldTrapFocus
                        useMediaGateway={this.props.useMediaGateway}
                    />
                </DropdownControlled>
            </div>
        );
    }
}

DropdownMenu.propTypes = {
    navigation: DirectNavigationModel.navigationType,
    sphereId: PropTypes.string.isRequired,
    onSelectNavSpace: PropTypes.func,
    noArrow: PropTypes.bool,
    label: PropTypes.node,
    customToggle: PropTypes.element,
    onOpen: PropTypes.func,
    onClose: PropTypes.func,
    useMediaGateway: PropTypes.bool,
};

export default withLifecycleLogging(DropdownMenu);
